import { useState } from 'react';
import { Group, Select, Stack, Table, Text, Title } from '@mantine/core';
import { useNavigate } from 'react-router';
import { VADE_DURUM_ETIKETI } from '@isg/validation';
import type { VadeDurumu } from '@isg/validation';
import { TEHLIKE_SINIFI_ETIKET } from '../i18n/tr';
import { BOS_ACIL_DURUM_SUZGECI, useAcilDurumListesi, useWorkplacesTumu } from '../api/queries';
import type { AcilDurumListeSuzgeci } from '../api/queries';
import { BosListeNotu, QueryStatus } from './parts';
import { Rozet, Sayfalama } from '../sozlesme';
import { tarihGoster, tamSayiGoster } from '../lib/format';

/**
 * ACİL DURUM — LİSTE (Kapsam §29; sayfa sözleşmesi P4 · E1).
 *
 * ⚠️ **LİSTE İŞYERİ EKSENLİDİR** (kurul listesi deseni): bahçedeki her aktif
 * işyeri bir satır — plan vadesi · kroki · ekip eksiği · tatbikat vadesi ·
 * toplanma noktası. Her sütun SUNUCUDA türetilir; ekran *"vadesi geçmiş
 * mi"* diye tarih karşılaştırmaz (vade tek yüklemden — C).
 * ⚠️ Süzgeç ve sayfalama SUNUCUDA (tanınmayan değer 400). *"Boş ≠ sıfır"*:
 * ekip eksiği `null` ise *"Tanımsız"* yazılır, 0 değil (madde 175).
 */
export function AcilDurumScreen(): React.JSX.Element {
  const [suzgec, setSuzgec] = useState<AcilDurumListeSuzgeci>(BOS_ACIL_DURUM_SUZGECI);
  const liste = useAcilDurumListesi(suzgec);
  const isyerleri = useWorkplacesTumu();
  const navigate = useNavigate();

  return (
    <Stack data-testid="acil-durum-ekrani">
      <Group justify="space-between">
        <Title order={3}>Acil durum</Title>
      </Group>
      <Group>
        <Select
          label="İşyeri"
          data-testid="acil-durum-isyeri-suzgeci"
          placeholder="Tümü"
          clearable
          searchable
          w={320}
          value={suzgec.workplaceId ?? null}
          onChange={(v) => setSuzgec((s) => ({ ...s, workplaceId: v ?? undefined, sayfa: 1 }))}
          data={(isyerleri.data ?? []).map((w) => ({ value: w.id, label: w.ad }))}
        />
      </Group>
      <QueryStatus queries={[liste]} />
      {liste.isSuccess && liste.data.satirlar.length === 0 && (
        <BosListeNotu mesaj="Bahçenizde aktif işyeri yok." />
      )}
      {liste.isSuccess && liste.data.satirlar.length > 0 && (
        <Table data-testid="acil-durum-tablosu" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>İşyeri</Table.Th>
              <Table.Th>Tehlike sınıfı</Table.Th>
              <Table.Th>Plan yenileme</Table.Th>
              <Table.Th>Kroki</Table.Th>
              <Table.Th>Ekip eksiği</Table.Th>
              <Table.Th>Tatbikat vadesi</Table.Th>
              <Table.Th>Toplanma noktası</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {liste.data.satirlar.map((s) => (
              <Table.Tr
                key={s.workplaceId}
                data-testid={`acil-durum-satiri-${s.workplaceId}`}
                style={{ cursor: 'pointer' }}
                onClick={() => void navigate(`/acil-durum/${s.workplaceId}`)}
              >
                <Table.Td>
                  <Text size="sm" fw={600}>
                    {s.workplaceAd}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {s.companyAd}
                  </Text>
                </Table.Td>
                <Table.Td>{TEHLIKE_SINIFI_ETIKET[s.tehlikeSinifi]}</Table.Td>
                <Table.Td>
                  <VadeRozeti
                    vade={s.planVadesi}
                    durum={s.planVadeDurumu}
                    testId={`acil-durum-plan-vade-${s.workplaceId}`}
                  />
                </Table.Td>
                <Table.Td>
                  <Rozet anlam={s.krokiVar ? 'olumlu' : 'dikkat'}>
                    {s.krokiVar ? 'Var' : 'Yok'}
                  </Rozet>
                </Table.Td>
                <Table.Td data-testid={`acil-durum-ekip-eksik-${s.workplaceId}`}>
                  {s.ekipEksik === null ? (
                    <Text size="xs" c="dimmed">
                      Tanımsız
                    </Text>
                  ) : (
                    <Rozet anlam={s.ekipEksik > 0 ? 'gecikmis' : 'olumlu'}>
                      {s.ekipEksik > 0 ? `${tamSayiGoster(s.ekipEksik)} eksik` : 'Tam'}
                    </Rozet>
                  )}
                </Table.Td>
                <Table.Td>
                  <VadeRozeti
                    vade={s.tatbikatVadesi}
                    durum={s.tatbikatVadeDurumu}
                    testId={`acil-durum-tatbikat-vade-${s.workplaceId}`}
                  />
                </Table.Td>
                <Table.Td>{tamSayiGoster(s.toplanmaNoktasiSayisi)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {liste.isSuccess && liste.data.toplam > liste.data.sayfaBoyutu && (
        <Sayfalama
          sayfa={liste.data.sayfa}
          sayfaBoyutu={liste.data.sayfaBoyutu}
          toplam={liste.data.toplam}
          onDegis={(sayfa) => setSuzgec((s) => ({ ...s, sayfa }))}
          testId="acil-durum-sayfalama"
        />
      )}
    </Stack>
  );
}

/** Vade rozeti — durum SUNUCUDAN; renk anlamı tek haritadan. */
export function VadeRozeti({
  vade,
  durum,
  testId,
}: {
  vade: string | null;
  durum: VadeDurumu;
  testId?: string;
}): React.JSX.Element {
  const anlam =
    durum === 'gecikmis'
      ? 'gecikmis'
      : durum === 'yaklasiyor'
        ? 'dikkat'
        : durum === 'gecerli'
          ? 'olumlu'
          : 'bilgi';
  return (
    <Group gap={6} wrap="nowrap" data-testid={testId}>
      <Rozet anlam={anlam}>{VADE_DURUM_ETIKETI[durum]}</Rozet>
      {vade && <Text size="xs">{tarihGoster(vade)}</Text>}
    </Group>
  );
}
